





























































































































































































import { Vue, Component, Prop, Ref, Watch } from "vue-property-decorator";
import IresDeviceStatusComponent from "@/components/devices/IresDeviceStatus.vue";
import QueuedRequestComponent from "./QueuedRequest.vue";
import { Panel, isEdgePanel } from "../../data/models/Panel";
import PanelStatusCardEntryComponent from "./PanelStatusEntry.vue";
import PanelControlComponent from "./PanelControl.vue";
import { PanelConfig, getPanelProgramName } from "@/data/models/PanelConfig";
import {
  ProgramChain,
  getMeterReadingInfo,
  isOverviewProgramChain,
  getProgramChainDurationDescription,
} from "@/data/models/ProgramChain";

import { getLastUpdateText, isInWarning } from "@/data/models/IresDevice";

import { formatFloat, formatKWh } from "@/utils/LocalisationHelper";
import { isActiveRun } from "@/data/models/RunOverview";
import {
  didIresDeviceTimeOut,
  isIdle,
  isPoweredOff,
} from "@/data/models/IresDevice";
import {
  formatGroundTemp,
  formatIntensity,
  formatWallTemp,
  getPanelSensorData,
} from "@/utils/SensorData";
import { ProgramChainAssignmentInfo } from "../devices/IresDeviceDetail.vue";
import PanelSettingsComponent from "./PanelSettings.vue";
import StateButtonComponent, { ButtonState } from "../common/StateButton.vue";
import { getHyDryPanelEventNumber, HyDryPanelEvent } from "@/data/models/HyDryEvent";
import { showAdditionalDeviceInfoAccess, showOpenSettingsIconAccess, viewIntensityAccess } from "@/permissions/UserPermissions";
import { COLOR_BLACK_TEXT, COLOR_BLACK_TEXT_DARK, COLOR_ERROR_TEXT, COLOR_ERROR_TEXT_DARK } from "@/utils/colors";
import { themed } from "@/utils/Theme";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component({
  components: {
    "state-button": StateButtonComponent,
    "queued-request": QueuedRequestComponent,
    "ires-device-status": IresDeviceStatusComponent,
    "panel-status-card-entry": PanelStatusCardEntryComponent,
    "panel-control": PanelControlComponent,
    "panel-settings": PanelSettingsComponent,
  },
})
export default class PanelStatusCardComponent extends Vue {
  @Prop() lastRefreshDate!: Date;
  @Prop() selectedProgramChain!: ProgramChain;
  @Prop() programChains!: Array<ProgramChain>;
  @Prop() selectedProgramChainDescription!: string;

  @Prop() showLatestIresDeviceInfo!: boolean;
  @Prop() panel!: Panel;

  @Prop() programChainAssignmentInfo!: ProgramChainAssignmentInfo | null;

  @Prop({ default: false }) isLoading!: boolean;
  @Prop({ default: null }) config!: PanelConfig | null;

  @Prop() isCurrentlyAssignedToProject!: boolean;
  @Prop({ default: true }) showIresDeviceControl!: boolean;
  @Prop({ default: false }) isScopedToProject!: boolean;
  @Prop({ default: false }) showDryingHistoryButton!: boolean;

  @Prop() programChainNumberLookup!: Map<number, number>;
  @Prop({ default: false }) showNoActiveProgramChainWarning!: boolean;

  @Ref("panelSettings")
  panelSettings!: PanelSettingsComponent;

  @Watch("selectedProgramChain", { immediate: true })
  onSelectedProgramChainChanged() {
    this.computeAdjustedProgramChainData();
  }

  isDisplayingGraph = false;
  showDryingHistoryButtonState = ButtonState.RED;

  programChainDurationDescription: string | null = null;

  /** The user whose view this shows: the chosen one while "Sicht auf" is active (ticket 2610052). */
  get activeUser() {
    return getEffectiveUser();
  }

  get isAllowedToViewSettingsIcon() {
    return showOpenSettingsIconAccess(this.activeUser);
  }

  get isAllowedToViewAdditionalInfo() {
    return showAdditionalDeviceInfoAccess(this.activeUser);
  }

  get isAllowedToViewIntensity() {
    return viewIntensityAccess(this.activeUser);
  }

  async setPanelOffline() {
    this.$emit("setIresDeviceOffline");
  }

  get showSetPanelOfflineButton() {
    if ("".length === 0) {
      return true;
    }
    if (this.isCurrentlyAssignedToProject) {
      return false;
    }

    if (!this.showLatestIresDeviceInfo) {
      return false;
    }

    const panel = this.panel;

    if (!this.panel) {
      return false;
    }

    const programChain = this.selectedProgramChain;

    if (!programChain) {
      return false;
    }

    const latestRun = programChain.runs[programChain.runs.length - 1];

    if (!latestRun || !isActiveRun(latestRun)) {
      return false;
    }

    return didIresDeviceTimeOut(panel, true);
  }

  get meterReadingInfoStartEnd() {
    if (this.showLatestIresDeviceInfo) {
      return null;
    }

    const meterReadingInfo = getMeterReadingInfo(this.selectedProgramChain);

    if (!meterReadingInfo) {
      return null;
    }

    return this.$t("panelDetail.metereReadingStartEnd", [
      formatFloat(meterReadingInfo.initialMeterReading, 1),
      formatFloat(meterReadingInfo.lastMeterReading, 1),
    ]).toString();
  }

  get meterReadingInfoPowerConsumed() {
    if (this.showLatestIresDeviceInfo) {
      return null;
    }

    const meterReadingInfo = getMeterReadingInfo(this.selectedProgramChain);

    if (!meterReadingInfo) {
      return null;
    }

    return formatKWh(this, meterReadingInfo.totalPowerConsumed, 1);
  }

  get durationLabel() {
    if (!this.duration) {
      return "";
    }

    return this.showLatestIresDeviceInfo && !isPoweredOff(this.panel)
      ? this.$t("panelDetail.runningSince")
      : this.$t("panelDetail.runTime");
  }

  get duration() {
    return this.programChainDurationDescription;
  }

  get noInfoAvailable() {
    return !this.isLoading && !this.panel.panelStatus;
  }

  get latestPanelInfoStyle() {
    return `visibility : ${
      this.showLatestIresDeviceInfo ? "visible" : "hidden"
    }`;
  }

  get panelStatus() {
    return this.panel.panelStatus;
  }

  get temperatureLabel() {
    return this.$t("panelDetail.temperature").toString();
  }

  get temperature() {
    const panelStatus =
      this.showLatestIresDeviceInfo && !isPoweredOff(this.panel)
        ? this.panelStatus
        : undefined;

    const sensorData = this.panelSensorData;
    const wallTemp = formatWallTemp(this, panelStatus, sensorData.wallTemp);

    if (isEdgePanel(this.panel)) {
      const groundTemp = formatGroundTemp(
        this,
        panelStatus,
        sensorData.groundTemp
      );

      return `${wallTemp} / ${groundTemp}`;
    } else {
      return wallTemp;
    }
  }

  get panelSensorData() {
    return getPanelSensorData(
      this.showLatestIresDeviceInfo ? this.panelStatus : undefined,
      this.selectedProgramChain
    );
  }

  get heatRatio() {
    if (this.showLatestIresDeviceInfo && this.isPanelIdle) {
      return "-";
    }

    return formatIntensity(
      this,
      this.showLatestIresDeviceInfo && !isPoweredOff(this.panel)
        ? this.panelStatus
        : undefined,
      this.panelSensorData.intensity
    );
  }

  created() {
    this.computeAdjustedProgramChainData();
  }

  async computeAdjustedProgramChainData() {
    if (!this.selectedProgramChain) {
      return null;
    }

    const openEnded =
      this.showLatestIresDeviceInfo && !isPoweredOff(this.panel);

    this.programChainDurationDescription = await getProgramChainDurationDescription(
      this,
      this.selectedProgramChain,
      openEnded
    );
  }

  formatTemperatuers(wallTemp?: number, groundTemp?: number) {
    const stringWallTemp = wallTemp ? wallTemp : "-";

    if (isEdgePanel(this.panel)) {
      const stringGroundTemp = groundTemp ? groundTemp : "-";
      return this.$t("panelDetail.formatTemperatureWallAndGround", [
        stringWallTemp,
        stringGroundTemp,
      ]).toString();
    } else {
      return this.$t("panelDetail.formatTemperatureWall", [
        stringWallTemp,
      ]).toString();
    }
  }

  get isPanelIdle(): boolean {
    return isIdle(this.panel);
  }

  get startStopButtonText(): string {
    return this.$t(
      this.isPanelIdle
        ? "panelDetail.actions.startPanel"
        : "panelDetail.actions.stopPanel"
    ).toString();
  }

  get dryingProgramTextColor() {
    return this.showNoActiveProgramChainWarning ? themed(COLOR_ERROR_TEXT, COLOR_ERROR_TEXT_DARK) : themed(COLOR_BLACK_TEXT, COLOR_BLACK_TEXT_DARK);
  }

  get dryingProgram() {
    if (this.showNoActiveProgramChainWarning) {
      return this.$t("panelDetail.noActiveProgramChainWarning");
    }

    if (!this.selectedProgramChain) {
      return "";
    }

    if (isOverviewProgramChain(this.selectedProgramChain)) {
      return "";
    }

    const firstRun = this.selectedProgramChain.runs[0];

    if (!firstRun) {
      return "";
    }

    if (this.isScopedToProject) {
      const chainNumber =
        this.programChains.length -
        this.programChains.indexOf(this.selectedProgramChain);

      return this.$t("panelDetail.formatChainAndProgram", [
        chainNumber,
        firstRun.programNumber,
      ]);
    }

    const programNumber = firstRun.programNumber;

    return getPanelProgramName(this, programNumber, this.panel, this.config);
  }

  get operatingStatus() {
    const panelStatus = this.panelStatus;

    if (!panelStatus) {
      return "";
    }

    switch (panelStatus.ipuStatus) {
      case 1:
        return this.$t("panelDetail.heating").toString();
      case 2:
        return this.$t("panelDetail.coooling").toString();
      case 3:
        return this.$t("panelDetail.waiting").toString();
      case 4:
        return this.$t("panelDetail.warning").toString();
      case 5:
        return this.$t("panelDetail.powerFail").toString();
      case 6:
        return this.$t("panelDetail.malfunction").toString();
      case 7:
        return this.$t("panelDetail.disconnected").toString();
      default:
        return this.$t("panelDetail.idle").toString();
    }
  }

  get programStepCycle() {
    const panelStatus = this.panelStatus;

    if (!panelStatus) {
      return "";
    }

    return `P${panelStatus.activeProgram}/S${panelStatus.currentStep}/Z${panelStatus.currentCycle}`;
  }

  get numRuns() {
    const selectedProgramChain = this.selectedProgramChain;

    if (!selectedProgramChain) {
      return "";
    }

    return selectedProgramChain.runs.length.toString();
  }

  get heatingInfo() {
    const panelStatus = this.panelStatus;

    if (!panelStatus) {
      return "";
    }

    return [
      panelStatus.timeSpanFirstStepHeatNorm,
      panelStatus.rateFirstStepHeatNorm,
      panelStatus.rateStepHeat,
      panelStatus.rateStepCool,
      panelStatus.rateStepHeatCool,
    ].join("/");
  }

  get event() {
    return this.panelStatus?.lastEvent?.toString();
  }

  get connectReason() {
    const connectReason = this.panelStatus?.connectReason;

    if (!connectReason || connectReason < 1 || connectReason > 10) {
      return "";
    }

    return this.$t(`panelDetail.connectReasonDescriptions.${connectReason}`);
  }

  get lastUpdate() {
    const panelStatus = this.panelStatus;

    if (!panelStatus) {
      return "";
    }

    return getLastUpdateText(this, this.lastRefreshDate, panelStatus, true);
  }

  /**
   * "Letztes Ereignis". IRES reads the raw device event; a client reads `lastClientEvent`, the newest
   * event the Cloud considered client-safe (`eventsToIgnoreAsLastEvent` in its `PanelRepository`).
   * The Cloud filter runs before storage, so reading the raw field here is what keeps IRES able to
   * see the events a client may not.
   */
  get lastEvent() {
    const lastEventNumber = this.isAllowedToViewAdditionalInfo
      ? this.panelStatus?.lastEvent
      : this.panelStatus?.lastClientEvent;

    if (!lastEventNumber) {
      return "";
    }

    const resourceId = 
      lastEventNumber === getHyDryPanelEventNumber(HyDryPanelEvent.MAX_HEAT_DURATION) && isInWarning(this.panel) 
      ? `panel.events.event${lastEventNumber}Warning` 
      : `panel.events.event${lastEventNumber}`;
    
    const resolved = this.$t(resourceId);

    return resolved === resourceId ? lastEventNumber.toString() : resolved;
  }

  onOpenSettings() {
    this.panelSettings.show();
  }

  showDryingHistory() {
    this.$emit("showDryingHistory");
  }
}
